import { Accordion, AccordionBody, AccordionHeader, Button, Card, CardBody, IconButton, Spinner } from '@material-tailwind/react';
import React, { useEffect, useState } from 'react';
import { ForceUpdateStats } from '../../services/backend';
import { PieChart, Pie, BarChart, Bar, AreaChart, Area, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { ChevronDownIcon, ChevronRightIcon, RefreshCwIcon } from 'lucide-react';
import { formatNumberWithCommas, priceToWords, roundDecimal, Notify } from '../../utils/functions';
import moment from 'moment';
import { BottomUXSpace } from '../../components';
import { COLORS } from '../../utils/enums';
import TotalRentBreakdown from './detail-modals/totalRentBreakdown';
import SecurityBreakdown from './detail-modals/securityBreakdown';
import SeatsDetailModal from './detail-modals/seatsDetailModal';
import { usePermissions } from '../../custom-hooks/use-permissions';
import HomeTabBar from './HomeTabBar';
import SupportDashboardView from './SupportDashboardView';
import { LayoutDashboardIcon, LifeBuoyIcon } from 'lucide-react';

const HOME_TABS = [
  { key: 'dashboard', label: 'Dashboard', icon: LayoutDashboardIcon },
  // Support Agents can't reach Home at all in the seeded roles, so this tab is for
  // Admins and Managers — who hold both FX1 and FX21:manage.
  { key: 'support', label: 'Support', icon: LifeBuoyIcon, permission: ['FX21', 'manage'] },
];



const COMMON_CARD_CLASS = 'shadow-none hover:cursor-pointer transition-all duration-300 rounded-lg border';

const Home = () => {
  const { can } = usePermissions();
  const [dashboardData, setDashboardData] = useState(null);
  const [expandBranches, setExpandBranches] = useState(0);
  const [expandBuildings, setExpandBuildings] = useState({});
  const [expandFloors, setExpandFloors] = useState({});
  const [loading, setLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const [totalRentBreakdownModal, setTotalRentBreakdownModal] = useState(false);
  const [securityBreakdownModal, setSecurityBreakdownModal] = useState(false);
  const [seatsDetailModal, setSeatsDetailModal] = useState(false);

  const visibleTabs = HOME_TABS.filter((tab) => !tab.permission || can(...tab.permission));
  const [activeTab, setActiveTab] = useState('dashboard');

  const handleExpandBranches = (value) => setExpandBranches(expandBranches === value ? -1 : value);

  const handleExpandBuildings = (branchIndex, buildingIndex) => {
    const key = `${branchIndex}-${buildingIndex}`;
    setExpandBuildings(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const handleExpandFloors = (branchIndex, buildingIndex, floorIndex) => {
    const key = `${branchIndex}-${buildingIndex}-${floorIndex}`;
    setExpandFloors(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  useEffect(() => {
    // Fetch dashboard data
    const fetchData = async (forceLoading = false) => {
      if (forceLoading) {
        setLoading(true);
      }
      try {
        const response = await ForceUpdateStats();
        setDashboardData(response.data.stats);
        // console.log('dashboardData', response.data.stats);
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    }
    fetchData(false);
  }, []);

  if (loading) {
    return <div className='min-h-[30vh] p-4 flex justify-center items-center'>
      <Spinner />
    </div>;
  }

  if (!dashboardData) {
    return <div className='min-h-[30vh] p-4 flex justify-center items-center'>
      <p className='text-gray-500'>No data available</p>
    </div>;
  }

  const totalSecurityThisMonth =
    dashboardData?.overview?.totalSecurityThisMonth ??
    0;
  const totalAdvanceThisMonth = dashboardData?.overview?.totalAdvanceThisMonth ?? 0;
  // Legacy (pre-invoice, contract-based) equivalents — only the hidden reference card below
  // should read these; the live card above uses totalSecurityThisMonth/totalAdvanceThisMonth.
  const legacyTotalSecurityThisMonth = dashboardData?.overview?.legacyTotalSecurityThisMonth ?? 0;
  const legacyTotalAdvanceThisMonth = dashboardData?.overview?.legacyTotalAdvanceThisMonth ?? 0;

  // Helper function to render stat cards
  const renderStatCards = (stats) => (
    <div className="grid grid-cols-5 gap-1 md:gap-2 md:mt-2">
      <Card color="gray" variant="gradient" className={`${COMMON_CARD_CLASS}`}>
        <CardBody className="px-1 py-3 md:p-3">
          <div className="font-bold text-xs text-center md:text-start ">Total Seats</div>
          <div className="text-xl font-bold text-center md:text-end">{stats.totalSeats}</div>
        </CardBody>
      </Card>
      <Card color="gray" variant="gradient" className={`${COMMON_CARD_CLASS}`}>
        <CardBody className="p-3">
          <div className="font-bold text-xs text-center md:text-start">Occupied</div>
          <div className="text-xl font-bold  text-center md:text-end">{stats.occupiedSeats}</div>
        </CardBody>
      </Card>
      <Card color="gray" variant="gradient" className={`${COMMON_CARD_CLASS}`}>
        <CardBody className="p-3">
          <div className="font-bold text-xs text-center md:text-start">Frozen</div>
          <div className="text-xl font-bold  text-center md:text-end">{stats.frozenSeats || 0}</div>
        </CardBody>
      </Card>
      <Card color="gray" variant="gradient" className={`${COMMON_CARD_CLASS}`}>
        <CardBody className="p-3">
          <div className="font-bold text-xs text-center md:text-start">Vacant</div>
          <div className="text-xl font-bold text-center md:text-end">{stats.vacantSeats}</div>
        </CardBody>
      </Card>
      <Card color="gray" variant="gradient" className={`${COMMON_CARD_CLASS}`}>
        <CardBody className="p-3">
          <div className="font-bold text-xs text-center md:text-start">Under Construction</div>
          <div className="text-xl font-bold text-center md:text-end">{stats.underConstructionSeats}</div>
        </CardBody>
      </Card>
    </div>
  );

  // Helper function to render financial stats
  const renderFinancialStats = (stats) => (
    <div className="grid grid-cols-2 gap-2 mt-2">
      <Card color="blue-gray" variant="gradient" className={`${COMMON_CARD_CLASS}`}>
        <CardBody className="p-3">
          <div className="font-bold text-xs">Total Rent</div>
          <div className="text-xl font-bold text-end">PKR {formatNumberWithCommas(roundDecimal(stats.totalRent, 2))}</div>
        </CardBody>
      </Card>
      <Card color="blue-gray" variant="gradient" className={`${COMMON_CARD_CLASS}`}>
        <CardBody className="p-3">
          <div className="font-bold text-xs">Occupancy Rate</div>
          <div className="text-xl font-bold text-end">{roundDecimal(stats.occupancyRate, 2)}%</div>
        </CardBody>
      </Card>
    </div>
  );

  // Helper function to render space type stats
  const renderSpaceTypeStats = (privateOffices, sharedSeats) => (
    <div className="grid grid-cols-2 gap-2 mt-2">
      <Card color="light-blue" variant="gradient" className={`${COMMON_CARD_CLASS}`}>
        <CardBody className="p-3">
          <div className="font-bold text-xs">Private Offices</div>
          <div className="flex flex-col md:flex-row justify-between mt-1">
            <div className="text-xs">
              <div>Count: {privateOffices.totalSeats}</div>
              <div>Occupied: {privateOffices.occupiedSeats}</div>
              <div>Frozen: {privateOffices.frozenSeats || 0}</div>
              <div>Vacant: {privateOffices.vacantSeats}</div>
            </div>
            <div className="text-lg font-bold text-end">PKR {formatNumberWithCommas(roundDecimal(privateOffices.totalRent, 2))}</div>
          </div>
        </CardBody>
      </Card>
      <Card color="light-blue" variant="gradient" className={`${COMMON_CARD_CLASS}`}>
        <CardBody className="p-3">
          <div className="font-bold text-xs">Shared Seats</div>
          <div className="flex flex-col md:flex-row  justify-between mt-1">
            <div className="text-xs">
              <div>Count: {sharedSeats.totalSeats}</div>
              <div>Occupied: {sharedSeats.occupiedSeats}</div>
              <div>Frozen: {sharedSeats.frozenSeats || 0}</div>
              <div>Vacant: {sharedSeats.vacantSeats}</div>
            </div>
            <div className="text-lg font-bold text-end">PKR {formatNumberWithCommas(roundDecimal(sharedSeats.totalRent, 2))}</div>
          </div>
        </CardBody>
      </Card>
    </div>
  );

  // Helper function to prepare rent breakdown data for pie chart
  const prepareRentBreakdownData = () => {
    const privateOfficesRent = dashboardData.overview.privateOfficesRent || 0;
    const sharedSeatsRent = dashboardData.overview.sharedSeatsRent || 0;

    return [
      { name: 'Private Offices', value: privateOfficesRent, color: COLORS.primary, legend: `PKR ${formatNumberWithCommas(privateOfficesRent)}` },
      { name: 'Shared Seats', value: sharedSeatsRent, color: COLORS.secondary, legend: `PKR ${formatNumberWithCommas(sharedSeatsRent)}` }
    ];
  };

  const prepareCustomersPieChartData = () => {
    const activeCustomers = dashboardData.overview.activeCustomers || 0;
    const inActiveCustomers = dashboardData.overview.inActiveCustomers || 0;
    return [
      { name: 'Active', value: activeCustomers, color: COLORS.success, legend: `${activeCustomers}` },
      { name: 'Inactive', value: inActiveCustomers, color: COLORS.error, legend: `${inActiveCustomers}` }
    ];
  }

  const prepareLeadsPieChartData = () => {
    const newLeads = dashboardData.overview.newLeads || 0;
    const leadsWon = dashboardData.overview.leadsWon || 0;
    const leadsDoNotContact = dashboardData.overview.leadsDoNotContact || 0;
    const leadsNegotiating = dashboardData.overview.leadsNegotiating || 0;
    const leadsOpen = dashboardData.overview.leadsOpen || 0;
    const leadsContacted = dashboardData.overview.leadsContacted || 0;
    const leadsQualified = dashboardData.overview.leadsQualified || 0;
    return [
      { name: 'New', value: newLeads, color: '#2196f3', legend: `${newLeads}` },
      { name: 'Contacted', value: leadsContacted, color: '#7c3aed', legend: `${leadsContacted}` },
      { name: 'Qualified', value: leadsQualified, color: '#9c27b0', legend: `${leadsQualified}` },
      { name: 'Negotiating', value: leadsNegotiating, color: '#e91e8c', legend: `${leadsNegotiating}` },
      { name: 'Won', value: leadsWon, color: '#8bc34a', legend: `${leadsWon}` },
      { name: 'Open', value: leadsOpen, color: '#009688', legend: `${leadsOpen}` },
      { name: 'Do Not Contact', value: leadsDoNotContact, color: '#ff5722', legend: `${leadsDoNotContact}` },
    ];
  }

  // Helper function to prepare branch occupancy data for bar chart
  const prepareBranchOccupancyData = () => {
    return dashboardData.branches.map(branch => ({
      name: branch.branchName,
      occupancyRate: roundDecimal(branch.occupancyRate, 2) || 0,
      vacancyRate: roundDecimal(branch.vacancyRate, 2) || 0,
      totalSeats: roundDecimal(branch.totalSeats, 2) || 0
    }));
  };

  const prepareSeatsOccupancyData = () => {
    return dashboardData.seatsHistoryByWeek.map(seats => ({
      name: moment(seats.date).format('DD MMM'),
      occupiedSeats: seats.occupiedSeats,
      frozenSeats: seats.frozenSeats || 0,
      vacantSeats: seats.vacantSeats,
      underConstructionSeats: seats.underConstructionSeats,
      totalSeats: seats.totalSeats
    }));
  }

  // Custom tooltip for pie chart
  const CustomPieTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      // console.log(payload);
      return (
        <div className="bg-white p-2 border rounded shadow">
          <p className="font-medium">{`${payload[0].name}`}</p>
          <p className="text-sm">{payload[0].payload.legend}</p>
          {/* <p className="text-xs">{`${(payload[0].percent * 100).toFixed(2)}%`}</p> */}
        </div>
      );
    }
    return null;
  };

  // Custom tooltip for bar chart
  const CustomBarTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-2 border rounded shadow">
          <p className="font-medium">{label}</p>
          <p className="text-sm text-blue-600">{`Occupancy: ${payload[0].value}%`}</p>
          <p className="text-sm text-red-600">{`Vacancy: ${payload[1].value}%`}</p>
          {/* <p className="text-xs">{`Total Seats: ${payload[2].value}`}</p> */}
        </div>
      );
    }
    return null;
  };

  const handleRefreshData = async () => {
    if (isRefreshing) return;
    try {
      setIsRefreshing(true);
      const response = await ForceUpdateStats();
      setDashboardData(response.data.stats);
      Notify('Data refreshed successfully', 'success');
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      const errorMsg = error.response?.data?.message || error.message || 'Unknown error';
      Notify(`Failed to refresh data. ${errorMsg}`, 'error');
    } finally {
      setIsRefreshing(false);
    }
  }
  return (
    <div className='p-4'>
      <div className={`flex flex-row items-center justify-between gap-4 mt-4`}>
        <h1 className='text-3xl font-thin'>Welcome to Pulse</h1>
        <Button variant='gradient' color='gray' onClick={handleRefreshData} disabled={isRefreshing} className='rounded-full hidden md:flex gap-4 items-center '>
          {isRefreshing ? <Spinner className='w-4 h-4' /> : <RefreshCwIcon className='w-4 h-4' />}
          <span>Refresh Data</span>
        </Button>
        <IconButton onClick={handleRefreshData} disabled={isRefreshing} className='rounded-full flex gap-4 items-center md:hidden'>
          {isRefreshing ? <Spinner className='w-4 h-4' /> : <RefreshCwIcon className='w-4 h-4' />}
        </IconButton>
      </div>

      <div className='mt-4'>
        <HomeTabBar tabs={visibleTabs} activeTab={activeTab} onChange={setActiveTab} />
      </div>

      {activeTab === 'support' && <SupportDashboardView />}

      {activeTab === 'dashboard' && (
      <>
      {can('FX1', 'overview_cards') && (
      <div className={`flex flex-col xl:flex-row gap-4 mt-4`}>
        {/* Seats Metrics */}
        <div className={` w-full xl:w-3/4`}>
          <Card color="transparent" variant="gradient" className={`w-full ${COMMON_CARD_CLASS} border`} onClick={() => setSeatsDetailModal(true)}>
            <CardBody className='p-0 pb-4'>
              <div className='flex justify-center items-center gap-4 w-full p-4 text-nowrap'>
                <hr className='my-2 border-gray-300 w-full' />
                <span className='text-xl md:text-3xl font-thin'>Seating Capacity</span>
                <hr className='my-2 border-gray-300 w-full' />
              </div>
              <div className='grid grid-cols-5 gap-4'>
                {
                  [
                    {
                      label: 'Total',
                      value: dashboardData.overview.totalSeats,
                    },
                    {
                      label: 'Occupied',
                      value: dashboardData.overview.occupiedSeats,
                    },
                    {
                      label: 'Frozen',
                      value: dashboardData.overview.frozenSeats || 0,
                    },
                    {
                      label: 'Vacant',
                      value: dashboardData.overview.vacantSeats,
                    },
                    {
                      label: 'Under Construction',
                      value: dashboardData.overview.underConstructionSeats,
                    }
                  ].map((item, index) => (
                    <div className='flex flex-col items-center' key={index}>
                      <div className={`font-bold text-xl`}>
                        {item.value}
                      </div>
                      <div className={`font-normal text-sm`}>
                        {item.label}
                      </div>
                    </div>
                  ))
                }
              </div>
              <div className='mt-4'>
                <ResponsiveContainer width="100%" height={260}>
                  <AreaChart
                    data={prepareSeatsOccupancyData()}
                    margin={{ top: 5, right: 30, left: 0, bottom: 5 }}
                  >
                    <defs>
                      <linearGradient id="colorOccupied" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor={COLORS.success} stopOpacity={0.3} />
                        <stop offset="95%" stopColor={COLORS.success} stopOpacity={0} />
                      </linearGradient>
                      <linearGradient id="colorFrozen" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#ff9800" stopOpacity={0.3} />
                        <stop offset="95%" stopColor="#ff9800" stopOpacity={0} />
                      </linearGradient>
                      <linearGradient id="colorVacant" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor={COLORS.error} stopOpacity={0.3} />
                        <stop offset="95%" stopColor={COLORS.error} stopOpacity={0} />
                      </linearGradient>
                      <linearGradient id="colorConstruction" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor={COLORS.primary} stopOpacity={0.3} />
                        <stop offset="95%" stopColor={COLORS.primary} stopOpacity={0} />
                      </linearGradient>
                      <linearGradient id="colorTotal" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor={COLORS.muted} stopOpacity={0.1} />
                        <stop offset="95%" stopColor={COLORS.muted} stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                    <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fill: '#6b7280', fontSize: 12 }} dy={10} />
                    <YAxis axisLine={false} tickLine={false} tick={{ fill: '#6b7280', fontSize: 12 }} dx={-10} />
                    <Tooltip contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }} />
                    <Legend iconType="circle" wrapperStyle={{ paddingTop: '20px' }} />
                    <Area type="monotone" dataKey="totalSeats" stroke={COLORS.muted} strokeWidth={2} fillOpacity={1} fill="url(#colorTotal)" name="Total" />
                    <Area type="monotone" dataKey="occupiedSeats" stroke={COLORS.success} strokeWidth={2} fillOpacity={1} fill="url(#colorOccupied)" activeDot={{ r: 6, strokeWidth: 0 }} name="Occupied" />
                    <Area type="monotone" dataKey="frozenSeats" stroke="#ff9800" strokeWidth={2} fillOpacity={1} fill="url(#colorFrozen)" activeDot={{ r: 6, strokeWidth: 0 }} name="Frozen" />
                    <Area type="monotone" dataKey="vacantSeats" stroke={COLORS.error} strokeWidth={2} fillOpacity={1} fill="url(#colorVacant)" name="Vacant" />
                    <Area type="monotone" dataKey="underConstructionSeats" stroke={COLORS.primary} strokeWidth={2} fillOpacity={1} fill="url(#colorConstruction)" name="Under Construction" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </CardBody>
          </Card>

        </div>
        {/* Total Overall Rent (legacy, pre-invoice booking-based calculation) — kept in the
            tree but hidden, not deleted, purely as a reference figure for comparison against
            the live invoice-based card below. Reads legacyTotalRent (etc.), not totalRent —
            these are two independent calculations computed in parallel on the backend. */}
        <Card color="light-green" variant="gradient" className={`hidden w-full xl:w-1/4 ${COMMON_CARD_CLASS}`} onClick={() => setTotalRentBreakdownModal(true)}>
          <CardBody className='flex flex-col justify-between h-full gap-2'>
            <div>
              <div>
                <div className={`font-bold text-sm`}>
                  Rent
                </div>
                <div className={`text-xl font-bold text-start`}>
                  PKR {formatNumberWithCommas(dashboardData.overview.legacyTotalRent)}
                </div>
                <div className={`text-xs font-medium`}>
                  {priceToWords(dashboardData.overview.legacyTotalRent)}
                </div>
              </div>
              <hr className='my-2 border-gray-300' />
              <div>
                <div className={`font-bold text-sm`}>
                  Security
                </div>
                <div className={`text-xl font-bold text-start`}>
                  PKR {formatNumberWithCommas(legacyTotalSecurityThisMonth)}
                </div>
                <div className={`text-xs font-medium`}>
                  {priceToWords(legacyTotalSecurityThisMonth)}
                </div>
              </div>
              <hr className='my-2 border-gray-300' />
              <div>
                <div className={`font-bold text-sm`}>
                  Advance
                </div>
                <div className={`text-xl font-bold text-start`}>
                  PKR {formatNumberWithCommas(legacyTotalAdvanceThisMonth)}
                </div>
                <div className={`text-xs font-medium`}>
                  {priceToWords(legacyTotalAdvanceThisMonth)}
                </div>
              </div>
              <hr className='my-2 border-gray-300' />
            </div>
            <div className={`animate-pulse ${COMMON_CARD_CLASS} p-2`}>
              <div className={`font-bold text-sm `}>
                Total
              </div>
              <div className={`text-xl font-bold text-start`}>
                PKR {formatNumberWithCommas(dashboardData.overview.legacyTotalRent + legacyTotalSecurityThisMonth)}
              </div>
              <div className={`text-xs font-medium`}>
                {priceToWords(dashboardData.overview.legacyTotalRent + legacyTotalSecurityThisMonth)}
              </div>
            </div>
          </CardBody>
        </Card>

        {/* Total Overall Rent (from invoices) — the one actually shown on the dashboard now. */}
        <Card color="light-green" variant="gradient" className={`w-full xl:w-1/4 ${COMMON_CARD_CLASS}`} onClick={() => setTotalRentBreakdownModal(true)}>
          <CardBody className='flex flex-col justify-between h-full gap-2'>
            <div>
              <div>
                <div className={`font-bold text-sm`}>
                  Rent
                </div>
                <div className={`text-xl font-bold text-start`}>
                  PKR {formatNumberWithCommas(dashboardData.overview.totalRent)}
                </div>
                <div className={`text-xs font-medium`}>
                  {priceToWords(dashboardData.overview.totalRent)}
                </div>
              </div>
              <hr className='my-2 border-gray-300' />
              <div
                className='cursor-pointer hover:opacity-80 transition-opacity'
                onClick={(e) => { e.stopPropagation(); setSecurityBreakdownModal(true); }}
              >
                <div className={`font-bold text-sm`}>
                  Security
                </div>
                <div className={`text-xl font-bold text-start`}>
                  PKR {formatNumberWithCommas(totalSecurityThisMonth)}
                </div>
                <div className={`text-xs font-medium`}>
                  {priceToWords(totalSecurityThisMonth)}
                </div>
              </div>
              <hr className='my-2 border-gray-300' />
              <div>
                <div className={`font-bold text-sm`}>
                  Advance
                </div>
                <div className={`text-xl font-bold text-start`}>
                  PKR {formatNumberWithCommas(totalAdvanceThisMonth)}
                </div>
                <div className={`text-xs font-medium`}>
                  {priceToWords(totalAdvanceThisMonth)}
                </div>
              </div>
              <hr className='my-2 border-gray-300' />
            </div>
            <div className={`animate-pulse ${COMMON_CARD_CLASS} p-2`}>
              <div className={`font-bold text-sm `}>
                Total
              </div>
              <div className={`text-xl font-bold text-start`}>
                PKR {formatNumberWithCommas(dashboardData.overview.totalRent + totalSecurityThisMonth)}
              </div>
              <div className={`text-xs font-medium`}>
                {priceToWords(dashboardData.overview.totalRent + totalSecurityThisMonth)}
              </div>
            </div>
          </CardBody>
        </Card>
      </div>
      )}

      {/* Invoice Summary (Moved down to be full width) */}
      {dashboardData?.overview?.invoiceStats && (
        <div className="shadow-none transition-all duration-300 rounded-lg border p-4 mt-4 bg-gradient-to-br from-white to-gray-50">
          <div className="font-thin text-2xl mb-4 text-gray-800">Invoice Summary for {moment().format('MMMM')}</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
            <div className="bg-white rounded-xl p-4 text-center shadow-sm border border-gray-100">
              <div className="text-xs text-gray-500 font-medium uppercase tracking-wider mb-1">Total Invoices</div>
              <div className="text-2xl font-bold text-gray-800">{dashboardData.overview.invoiceStats.invoiceCount || 0}</div>
            </div>
            <div className="bg-blue-50/50 rounded-xl p-4 text-center shadow-sm border border-blue-100">
              <div className="text-xs text-blue-600 font-medium uppercase tracking-wider mb-1">Total Invoiced</div>
              <div className="text-lg font-bold text-blue-700">PKR {formatNumberWithCommas(dashboardData.overview.invoiceStats.totalInvoicedAmount || 0)}</div>
            </div>
            <div className="bg-green-50/50 rounded-xl p-4 text-center shadow-sm border border-green-100">
              <div className="text-xs text-green-600 font-medium uppercase tracking-wider mb-1">Total Paid</div>
              <div className="text-lg font-bold text-green-700">PKR {formatNumberWithCommas(dashboardData.overview.invoiceStats.totalPaidAmount || 0)}</div>
            </div>
            <div className="bg-red-50/50 rounded-xl p-4 text-center shadow-sm border border-red-100">
              <div className="text-xs text-red-600 font-medium uppercase tracking-wider mb-1">Outstanding</div>
              <div className="text-lg font-bold text-red-700">PKR {formatNumberWithCommas(dashboardData.overview.invoiceStats.totalOutstandingAmount || 0)}</div>
            </div>
          </div>

          <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100">
            <div className="flex flex-col gap-2">
              <div className="flex justify-between items-end mb-1">
                <div>
                  <div className="text-sm text-gray-500 font-medium">Collection Progress</div>
                  <div className="text-xl font-bold text-gray-800">
                    {dashboardData.overview.invoiceStats.totalInvoicedAmount > 0
                      ? ((dashboardData.overview.invoiceStats.totalPaidAmount / dashboardData.overview.invoiceStats.totalInvoicedAmount) * 100).toFixed(1)
                      : 0}% Collected
                  </div>
                </div>
              </div>
              <div className="w-full bg-red-100 rounded-full h-6 overflow-hidden flex shadow-inner">
                <div
                  className="bg-green-500 h-full transition-all duration-1000 ease-in-out"
                  style={{ width: `${dashboardData.overview.invoiceStats.totalInvoicedAmount > 0 ? (dashboardData.overview.invoiceStats.totalPaidAmount / dashboardData.overview.invoiceStats.totalInvoicedAmount) * 100 : 0}%` }}
                ></div>
              </div>
              <div className="flex justify-between mt-2 text-sm font-medium text-gray-600">
                <div className="flex items-center gap-2"><div className="w-3 h-3 rounded-full bg-green-500"></div> Paid (PKR {formatNumberWithCommas(dashboardData.overview.invoiceStats.totalPaidAmount || 0)})</div>
                <div className="flex items-center gap-2"><div className="w-3 h-3 rounded-full bg-red-400"></div> Outstanding (PKR {formatNumberWithCommas(dashboardData.overview.invoiceStats.totalOutstandingAmount || 0)})</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {can('FX1', 'pie_charts') && (
      <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-4'>
        <div className={`${COMMON_CARD_CLASS} p-4`}>
          <div className="font-thin text-2xl mb-2">Leads ({dashboardData?.overview?.totalLeads || 0})</div>
          <ResponsiveContainer width="100%" height={260}>
            <PieChart>
              <Pie
                data={prepareLeadsPieChartData()}
                cx="50%"
                cy="50%"
                innerRadius={0}
                outerRadius={80}
                fill="#000"
                paddingAngle={2}
                dataKey="value"
              // label={({ name, percent }) => `${(percent * 100).toFixed(0)}%`}
              >
                {prepareLeadsPieChartData().map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip content={<CustomPieTooltip />} />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </div>
        <div className={`${COMMON_CARD_CLASS} p-4`}>
          <div className="font-thin text-2xl mb-2">Customers ({dashboardData?.overview?.totalCustomers || 0})</div>
          <ResponsiveContainer width="100%" height={260}>
            <PieChart>
              <Pie
                data={prepareCustomersPieChartData()}
                cx="50%"
                cy="50%"
                innerRadius={0}
                outerRadius={80}
                fill="#000"
                paddingAngle={2}
                dataKey="value"
                label={({ name, percent }) => `${(percent * 100).toFixed(0)}%`}
              >
                {prepareCustomersPieChartData().map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip content={<CustomPieTooltip />} />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </div>
        <div className={`${COMMON_CARD_CLASS} p-4`}>
          <div className="font-thin text-2xl mb-2">Rent Breakdown</div>
          <ResponsiveContainer width="100%" height={260}>
            <PieChart>
              <Pie
                data={prepareRentBreakdownData()}
                cx="50%"
                cy="50%"
                innerRadius={0}
                outerRadius={80}
                fill="#000"
                paddingAngle={2}
                dataKey="value"
                label={({ name, percent }) => `${(percent * 100).toFixed(0)}%`}
              >
                {prepareRentBreakdownData().map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} legend={entry.legend} />
                ))}
              </Pie>
              <Tooltip content={<CustomPieTooltip />} />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>
      )}
      {can('FX1', 'history_metrics') && (
      <div className={`flex flex-col lg:flex-row gap-4 mt-4`}>
        <div className={`w-full lg:w-2/4`}>
          <div className=''>
            {/* <Card color="transparent" variant="gradient" className={`w-full ${COMMON_CARD_CLASS} border`}>
              <CardBody className='flex flex-col justify-between py-3'>
                <div className={`text-3xl font-bold text-center`}>
                  {dashboardData.overview.totalNewContractsThisMonth}
                </div>
                <div className={`text-center`}>
                  New Contracts <br />
                  <span className='text-xs text-gray-500'>{moment().format('MMMM YYYY')}</span>
                </div>
              </CardBody>
            </Card> */}
            {/* Customers pie chart */}

          </div>
          {/* Rent History Bar Chart */}
          <div className={`${COMMON_CARD_CLASS} p-4`}>
            <div className="font-thin text-2xl mb-2">Rent History</div>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart
                data={(() => {
                  // Generate last 12 months
                  const months = [];

                  for (let i = 11; i >= 0; i--) {
                    const monthDate = moment().subtract(i, 'months');
                    const monthKey = monthDate.format('YYYY-MM');

                    // Find matching data from rentHistoryByMonth
                    const monthData = dashboardData.rentHistoryByMonth?.find(item =>
                      moment(item.month).format('YYYY-MM') === monthKey
                    );

                    months.push({
                      name: monthDate.format('MMM YYYY'),
                      totalRent: monthData?.totalRent || 0
                    });
                  }

                  return months;
                })()}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis
                  label={{ value: 'Rent (PKR)', angle: -90, position: 'insideLeft' }}
                  tickFormatter={(value) => `${(value / 1000000).toFixed(1)}M`}
                />
                <Tooltip
                  content={({ active, payload, label }) => {
                    if (active && payload && payload.length) {
                      return (
                        <div className="bg-white p-2 border rounded shadow">
                          <p className="font-medium">{label}</p>
                          <p className="text-sm">PKR {formatNumberWithCommas(payload[0].value)}</p>
                        </div>
                      );
                    }
                    return null;
                  }}
                />
                <Legend />
                <Bar dataKey="totalRent" name="Monthly Rent" fill="#00C49F" />
              </BarChart>
            </ResponsiveContainer>
          </div>
          {/* Occupancy Progress Card - Overall Metrics */}
          <div className={`${COMMON_CARD_CLASS} p-4 mt-4`}>
            <div className="font-thin text-2xl mb-2">Overall Metrics</div>
            <div className="mb-2">
              <div className="flex justify-between mb-1">
                <div className="font-thin text-sm">Occupancy Rate</div>
                <div className="font-thin text-sm">{roundDecimal(dashboardData.overview.occupancyRate, 2) || 0}%</div>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2.5">
                <div
                  className="bg-green-600 h-2.5 rounded-full"
                  style={{ width: `${dashboardData.overview.occupancyRate || 0}%` }}
                ></div>
              </div>
            </div>

            <div className="mb-2">
              <div className="flex justify-between mb-1">
                <div className="font-thin text-sm">Vacancy Rate</div>
                <div className="font-thin text-sm">{roundDecimal(dashboardData.overview.vacancyRate, 2) || 0}%</div>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2.5">
                <div
                  className="bg-red-500 h-2.5 rounded-full"
                  style={{ width: `${dashboardData.overview.vacancyRate || 0}%` }}
                ></div>
              </div>
            </div>

            <div>
              <div className="flex justify-between mb-1">
                <div className="font-thin text-sm">Construction Progress</div>
                <div className="font-thin text-sm">{roundDecimal(dashboardData.overview.constructionProgress, 2) || 0}%</div>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2.5">
                <div
                  className="bg-blue-600 h-2.5 rounded-full"
                  style={{ width: `${dashboardData.overview.constructionProgress || 0}%` }}
                ></div>
              </div>
            </div>
          </div>
        </div>
        {/* Total Rent Breakdown Piechart + barchart + overall metrics */}
        <div className={`w-full lg:w-2/4 flex flex-col gap-4`}>
          {/* Total Security this Month */}
          <Card color="transparent" variant="gradient" className={`w-full ${COMMON_CARD_CLASS} mb-4 border`}>
            <CardBody>
              <div className={`text-green-500`}>
                <div className={`font-bold text-sm`}>
                  Total Incomming Amount this Month
                </div>
                <div className={`flex justify-end`}>
                  <div>
                    <div className={`text-lg font-normal w-full text-end`}>
                      PKR {formatNumberWithCommas(dashboardData.overview.totalRent)}
                    </div>
                    <div className={`text-lg font-normal w-full text-end`}>
                      + PKR {formatNumberWithCommas(totalSecurityThisMonth)}
                    </div>
                    <hr className='my-2 border-gray-300' />
                    <div className={`text-3xl font-bold w-full text-end`}>
                      PKR {formatNumberWithCommas(totalSecurityThisMonth + dashboardData.overview.totalRent)}
                    </div>
                  </div>
                </div>
              </div>
              <hr className='my-2 border-gray-300' />
              <div className={`text-xs font-medium text-green-500`}>
                {priceToWords(totalSecurityThisMonth + dashboardData.overview.totalRent)}
              </div>

            </CardBody>
          </Card>


          {/* Branch Occupancy Bar Chart */}
          <div className={`${COMMON_CARD_CLASS} p-4`}>
            <div className="font-thin text-2xl mb-2">Branch Occupancy</div>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart
                data={prepareBranchOccupancyData()}
              // margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis label={{ value: 'Percentage (%)', angle: -90, position: 'insideLeft' }} />
                <Tooltip content={<CustomBarTooltip />} />
                <Legend />
                <Bar dataKey="occupancyRate" name="Occupancy" fill="#74b9ff" /> {/* fill="#82ca9d" */}
                <Bar dataKey="vacancyRate" name="Vacancy" fill="#ff7675" />
                {/* <Bar dataKey="totalSeats" name="Total Seats" fill="#74b9ff" /> */}
              </BarChart>
            </ResponsiveContainer>
          </div>


        </div>
      </div>
      )}
      {can('FX1', 'branch_summary') && (
        <>
      <hr className='my-4 border-gray-300' />
      <hr className='my-4 border-gray-300' />
      <hr className='my-4 border-gray-300' />
      <div className='text-center bg-gray-200'>
        <span className='text-2xl font-thin'>Branch Wise Summary</span>
      </div>
      <hr className='my-4 border-gray-300' />
      <hr className='my-4 border-gray-300' />
      <hr className='my-4 border-gray-300' />
      {/* Branch Wise Summary */}
      {
        dashboardData.branches.map((branch, branchIndex) => (
          <Accordion
            key={branchIndex}
            open={expandBranches === branchIndex}
            className='mt-2 rounded-lg'
          >
            <AccordionHeader
              className={`border rounded-lg px-2 md:px-4 py-2 md:py-4 transition-colors ${expandBranches === branchIndex ? "text-blue-500 hover:!text-blue-700 rounded-t- rounded-b-none" : ""}`}
              onClick={() => handleExpandBranches(branchIndex)}
            >
              <div className={`font-thin text-sm md:text-md flex items-center gap-4`}>
                {expandBranches === branchIndex ? <ChevronDownIcon size={16} /> : <ChevronRightIcon size={16} />}
                <span>{branch.branchName}</span>
              </div>
            </AccordionHeader>
            <AccordionBody className="px-1 md:px-2 border-s-4 md:border-s-8 ">
              { }
              {renderStatCards(branch)}
              {renderFinancialStats(branch)}

              <div className="mt-3 font-thin text-lg">Buildings</div>

              {/* Buildings */}
              {branch.buildings.map((building, buildingIndex) => {
                const buildingKey = `${branchIndex}-${buildingIndex}`;
                const isBuildingOpen = expandBuildings[buildingKey];

                return (
                  <Accordion
                    key={buildingIndex}
                    open={isBuildingOpen}
                    className=' rounded-lg mt-2'
                  >
                    <AccordionHeader
                      className={`border rounded-lg px-2 md:px-4 py-2 md:py-4 transition-colors text-base ${isBuildingOpen ? "text-blue-500 hover:!text-blue-700 rounded-t-lg rounded-b-none" : ""}`}
                      onClick={() => handleExpandBuildings(branchIndex, buildingIndex)}
                    >
                      <div className={`font-thin text-sm md:text-md flex items-center gap-4`}>
                        {isBuildingOpen ? <ChevronDownIcon size={16} /> : <ChevronRightIcon size={16} />}
                        <span>{building.buildingName}</span>
                      </div>

                    </AccordionHeader>
                    <AccordionBody className="px-1 md:px-2 border-s-4 md:border-s-8 ">
                      {/* Building Stats */}
                      {renderStatCards(building)}

                      <Card color="blue-gray" variant="gradient" className={`${COMMON_CARD_CLASS} mt-2`}>
                        <CardBody className="p-3">
                          <div className="font-thin text-xs">Total Rent</div>
                          <div className="text-xl font-bold text-end">PKR {formatNumberWithCommas(roundDecimal(building.totalRent, 2))}</div>
                        </CardBody>
                      </Card>

                      <div className="mt-3 font-thin text-lg">Floors</div>

                      {/* Floors */}
                      {building.floors.map((floor, floorIndex) => {
                        const floorKey = `${branchIndex}-${buildingIndex}-${floorIndex}`;
                        const isFloorOpen = expandFloors[floorKey];

                        return (
                          <Accordion
                            key={floorIndex}
                            open={isFloorOpen}
                            className='rounded-lg mt-2'
                          >
                            <AccordionHeader
                              className={`border px-2 md:px-4 py-2 md:py-4 rounded-lg transition-colors text-sm ${isFloorOpen ? "text-blue-500 hover:!text-blue-700 rounded-t-lg rounded-b-none" : ""}`}
                              onClick={() => handleExpandFloors(branchIndex, buildingIndex, floorIndex)}
                            >
                              <div className={`font-thin text-sm md:text-md flex items-center gap-4`}>
                                {isFloorOpen ? <ChevronDownIcon size={16} /> : <ChevronRightIcon size={16} />}
                                <span>Floor {floor.floorNumber} ({floor.floorCode}) - {floor.status}</span>
                              </div>
                            </AccordionHeader>
                            <AccordionBody className="px-1 md:px-2 border-s-4 md:border-s-8 ">
                              {/* Floor Stats */}
                              {renderStatCards(floor)}

                              <Card color="blue-gray" variant="gradient" className={`${COMMON_CARD_CLASS} mt-2`}>
                                <CardBody className="p-3">
                                  <div className="font-thin text-xs">Total Rent</div>
                                  <div className="text-xl font-bold text-end">PKR {formatNumberWithCommas(roundDecimal(floor.totalRent, 2))}</div>
                                </CardBody>
                              </Card>

                              {/* Space Types */}
                              <div className="mt-3 font-thin text-md">Space Types</div>

                              {renderSpaceTypeStats(floor.privateOffices, floor.sharedSeats)}
                            </AccordionBody>
                          </Accordion>
                        );
                      })}
                    </AccordionBody>
                  </Accordion>
                );
              })}
            </AccordionBody>
          </Accordion>
        ))
      }
      </>
      )}
      </>
      )}

      <BottomUXSpace size={32} />


      {
        securityBreakdownModal && (
          <SecurityBreakdown isOpen={securityBreakdownModal} onClose={() => setSecurityBreakdownModal(false)} />
        )
      }

      {
        totalRentBreakdownModal && (
          <TotalRentBreakdown isOpen={totalRentBreakdownModal} onClose={() => setTotalRentBreakdownModal(false)} />
        )
      }
      {
        seatsDetailModal && (
          <SeatsDetailModal isOpen={seatsDetailModal} onClose={() => setSeatsDetailModal(false)} />
        )
      }
    </div>
  );
};

export default Home;